import { useEffect, useRef, useState } from "react";
import type { CoachMessage } from "../schema";
import { renderProse } from "../prose";

interface Props {
  messages: CoachMessage[];
  streaming: boolean;
  streamingText: string;
}

function formatTime(ms: number): string {
  if (!ms) return "";
  const d = new Date(ms);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// Auto-scroll heuristic — if the user is within this many px of the bottom,
// we keep auto-scrolling; otherwise we let them stay where they are.
const PIN_THRESHOLD_PX = 80;

export function MessageList({ messages, streaming, streamingText }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(true);

  // Track scroll position to decide auto-scroll.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setPinned(distanceFromBottom <= PIN_THRESHOLD_PX);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Auto-scroll when messages/streaming text change AND we're still pinned.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !pinned) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, streamingText, streaming, pinned]);

  const empty = messages.length === 0 && !streaming;

  return (
    <div className="coach__messages" ref={scrollRef}>
      {empty ? (
        <div className="coach__empty">
          <div className="coach__empty-mark">¶</div>
          <p className="coach__empty-prompt">
            Ask the coach about your gaps, layers, or a recent incident.
          </p>
          <p className="coach__empty-hint">
            The coach can see your canvas — every phase, every score, every
            gap — but cannot edit it. To change an answer, use{" "}
            <em>Review now</em> on the dashboard.
          </p>
        </div>
      ) : (
        <ol className="coach__entries">
          {messages.map((m, i) => (
            <Entry key={m.id} msg={m} index={i + 1} />
          ))}
          {streaming && (
            <li className="coach__entry coach__msg--assistant coach__entry--streaming">
              <header className="coach__entry-meta">
                <span className="coach__entry-attr">Coach</span>
                <span className="coach__entry-time">streaming…</span>
              </header>
              <div className="coach__entry-body">
                {renderProse(streamingText)}
                <span className="coach__cursor" aria-hidden="true" />
              </div>
            </li>
          )}
        </ol>
      )}
    </div>
  );
}

function Entry({ msg, index }: { msg: CoachMessage; index: number }) {
  const cls =
    msg.role === "user" ? "coach__msg--user" : "coach__msg--assistant";
  const attr = msg.role === "user" ? "You" : "Coach";
  return (
    <li className={`coach__entry ${cls}`} data-index={String(index).padStart(3, "0")}>
      <header className="coach__entry-meta">
        <span className="coach__entry-attr">{attr}</span>
        <span className="coach__entry-time">{formatTime(msg.createdAt)}</span>
      </header>
      <div className="coach__entry-body">{renderProse(msg.content)}</div>
    </li>
  );
}
